import { useState, useEffect } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Switch,
  Button,
  Group,
  Loader,
  Center,
  Alert,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  updateGlobalEligibility,
  GlobalEligibilityRule,
} from "../../../services/prefixService";
import { insuranceProvidersService } from "../../../services/admin/insuranceProviders.service";

interface RowState {
  insuranceProviderId: string;
  name: string;
  allowSplitBilling: boolean;
}

export default function GlobalInsuranceEligibilityPage() {
  const [rows, setRows] = useState<RowState[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data: providers, isLoading } = useQuery({
    queryKey: ["insuranceProviders"],
    queryFn: () => insuranceProvidersService.getAll(),
  });

  useEffect(() => {
    if (providers) {
      setRows(
        providers.map((p) => ({
          insuranceProviderId: p.id,
          name: p.name,
          allowSplitBilling: false,
        }))
      );
    }
  }, [providers]);

  const saveMutation = useMutation({
    mutationFn: (rules: GlobalEligibilityRule[]) =>
      updateGlobalEligibility(rules),
    onSuccess: () => {
      setSaveError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  const toggleRow = (providerId: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.insuranceProviderId === providerId
          ? { ...r, allowSplitBilling: !r.allowSplitBilling }
          : r
      )
    );
  };

  const handleSave = () => {
    const rules: GlobalEligibilityRule[] = rows.map((r) => ({
      insuranceProviderId: r.insuranceProviderId,
      allowSplitBilling: r.allowSplitBilling,
    }));
    saveMutation.mutate(rules);
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: 900, margin: "0 auto" }}>
        <Stack gap="lg">
          <div>
            <Title order={2}>Global Insurance Eligibility</Title>
            <Text size="sm" c="dimmed" mt="xs">
              Set the default TCPC split billing eligibility for each insurance
              carrier. These defaults apply globally unless overridden at the
              prefix or physician level.
            </Text>
          </div>

          {saveError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {saveError}
            </Alert>
          )}
          {saved && (
            <Alert color="green">Global eligibility saved successfully.</Alert>
          )}

          {isLoading ? (
            <Center p="xl">
              <Loader />
            </Center>
          ) : (
            <Paper withBorder style={{ overflow: "hidden" }}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Insurance Carrier</Table.Th>
                    <Table.Th>Allow Split Billing (TCPC)</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((row) => (
                    <Table.Tr key={row.insuranceProviderId}>
                      <Table.Td>
                        <Text size="sm">{row.name}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Switch
                          checked={row.allowSplitBilling}
                          onChange={() => toggleRow(row.insuranceProviderId)}
                          color={row.allowSplitBilling ? "green" : "red"}
                          label={
                            row.allowSplitBilling ? "Allowed" : "Not Allowed"
                          }
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          <Group justify="flex-end">
            <Button
              onClick={handleSave}
              loading={saveMutation.isPending}
              disabled={isLoading}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Box>
    </Container>
  );
}
